/**
 * Handles ChunkLoadError that occurs when cached JS bundles
 * are stale after a new deployment.
 *
 * Must be initialized BEFORE React mounts.
 */
export function initChunkErrorHandler() {
  const isChunkError = (message: string): boolean => {
    return (
      message.includes('Loading chunk') ||
      message.includes('ChunkLoadError') ||
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('error loading dynamically imported module') ||
      message.includes('Failed to load module script') ||
      message.includes('Importing a module script failed') ||
      message.includes('Unable to preload CSS') ||
      message.includes('dynamically imported module')
    );
  };

  const RELOAD_KEY = 'chunk-error-reload-ts';
  const RELOAD_COOLDOWN_MS = 10000; // 10 seconds

  const shouldReload = (): boolean => {
    const lastReload = sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();
    
    if (!lastReload || now - parseInt(lastReload, 10) > RELOAD_COOLDOWN_MS) {
      sessionStorage.setItem(RELOAD_KEY, now.toString());
      return true;
    }
    console.warn('[ChunkLoadError] Skipping reload - cooldown active');
    return false;
  };

  // Handle synchronous errors
  window.addEventListener('error', (event) => {
    const msg = String(event?.message || '');
    if (isChunkError(msg)) {
      console.warn('[ChunkLoadError] Detected stale bundle');
      event.preventDefault();
      if (shouldReload()) window.location.reload();
    }
  });

  // Handle Promise rejections (for dynamic imports)
  window.addEventListener('unhandledrejection', (event) => {
    const msg = String(event?.reason?.message || event?.reason || '');
    if (isChunkError(msg)) {
      console.warn('[ChunkLoadError] Detected in unhandled rejection');
      event.preventDefault();
      if (shouldReload()) window.location.reload();
    }
  });
}
